/**
 * Centralized Query Keys for TanStack Query
 * No hardcoded query key strings should be used elsewhere
 */

export const QueryKeys = {
  // Authentication
  auth: {
    all: ["auth"] as const,
    session: () => [...QueryKeys.auth.all, "session"] as const,
    permissions: () => [...QueryKeys.auth.all, "permissions"] as const,
    permissionVersion: () => [...QueryKeys.auth.all, "permissionVersion"] as const,
  },

  // Users
  users: {
    all: ["users"] as const,
    lists: () => [...QueryKeys.users.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.users.lists(), params] as const,
    details: () => [...QueryKeys.users.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.users.details(), id] as const,
  },

  // Platform Owner — Tenants
  platformOwnerTenants: {
    all: ["platformOwnerTenants"] as const,
    lists: () => [...QueryKeys.platformOwnerTenants.all, "list"] as const,
    list: (params?: unknown) =>
      [...QueryKeys.platformOwnerTenants.lists(), params] as const,
    details: () => [...QueryKeys.platformOwnerTenants.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.platformOwnerTenants.details(), id] as const,
    domains: (id: string) =>
      [...QueryKeys.platformOwnerTenants.all, "domains", id] as const,
    branding: (id: string) =>
      [...QueryKeys.platformOwnerTenants.all, "branding", id] as const,
    authSettings: (id: string) =>
      [...QueryKeys.platformOwnerTenants.all, "auth-settings", id] as const,
    localization: (id: string) =>
      [...QueryKeys.platformOwnerTenants.all, "localization", id] as const,
    aiSettings: (id: string) =>
      [...QueryKeys.platformOwnerTenants.all, "ai-settings", id] as const,
    countries: () => [...QueryKeys.platformOwnerTenants.all, "countries"] as const,
  },

  // Platform Owner — Bulk Import
  platformOwnerBulkImport: {
    all: ["platformOwnerBulkImport"] as const,
    academicYears: (tenantId: string) =>
      [...QueryKeys.platformOwnerBulkImport.all, "academicYears", tenantId] as const,
    classes: (tenantId: string, academicYearId: string) =>
      [
        ...QueryKeys.platformOwnerBulkImport.all,
        "classes",
        tenantId,
        academicYearId,
      ] as const,
  },

  // Platform Owner — Legacy Data Migration
  platformOwnerLegacyMigration: {
    all: ["platformOwnerLegacyMigration"] as const,
    status: (tenantId: string, migrationId: string) =>
      [
        ...QueryKeys.platformOwnerLegacyMigration.all,
        "status",
        tenantId,
        migrationId,
      ] as const,
    statusV2: (tenantId: string, migrationId: string) =>
      [
        ...QueryKeys.platformOwnerLegacyMigration.all,
        "statusV2",
        tenantId,
        migrationId,
      ] as const,
  },

  // Platform Owner — Migration Management console (cross-tenant history/health)
  platformOwnerMigrations: {
    all: ["platformOwnerMigrations"] as const,
    list: (filters: unknown) =>
      [...QueryKeys.platformOwnerMigrations.all, "list", filters] as const,
    summary: (filters: unknown) =>
      [...QueryKeys.platformOwnerMigrations.all, "summary", filters] as const,
    detail: (migrationId: string) =>
      [...QueryKeys.platformOwnerMigrations.all, "detail", migrationId] as const,
  },

  // Roles
  roles: {
    all: ["roles"] as const,
    lists: () => [...QueryKeys.roles.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.roles.lists(), params] as const,
    details: () => [...QueryKeys.roles.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.roles.details(), id] as const,
  },

  // Academic Years
  academicYears: {
    all: ["academicYears"] as const,
    lists: () => [...QueryKeys.academicYears.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.academicYears.lists(), params] as const,
    details: () => [...QueryKeys.academicYears.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.academicYears.details(), id] as const,
  },

  // Classes
  classes: {
    all: ["classes"] as const,
    lists: () => [...QueryKeys.classes.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.classes.lists(), params] as const,
    details: () => [...QueryKeys.classes.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.classes.details(), id] as const,
    byAcademicYear: (academicYearId: string | null | undefined) =>
      [...QueryKeys.classes.lists(), "year", academicYearId ?? "unspecified"] as const,
  },

  // Sections
  sections: {
    all: ["sections"] as const,
    lists: () => [...QueryKeys.sections.all, "list"] as const,
    byAcademicYear: (academicYearId: string | null | undefined) =>
      [...QueryKeys.sections.lists(), "year", academicYearId ?? "unspecified"] as const,
    detail: (id: string) => [...QueryKeys.sections.all, "detail", id] as const,
  },

  sectionPresets: {
    all: ["sectionPresets"] as const,
    lists: () => [...QueryKeys.sectionPresets.all, "list"] as const,
    list: () => [...QueryKeys.sectionPresets.lists()] as const,
  },

  // Subjects
  subjects: {
    all: ["subjects"] as const,
    lists: () => [...QueryKeys.subjects.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.subjects.lists(), params] as const,
    details: () => [...QueryKeys.subjects.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.subjects.details(), id] as const,
  },

  // Permissions
  permissions: {
    all: ["permissions"] as const,
    lists: () => [...QueryKeys.permissions.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.permissions.lists(), params] as const,
  },

  // Audit Logs
  auditLogs: {
    all: ["auditLogs"] as const,
    lists: () => [...QueryKeys.auditLogs.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.auditLogs.lists(), params] as const,
    details: () => [...QueryKeys.auditLogs.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.auditLogs.details(), id] as const,
  },

  // Countries
  countries: {
    all: ["countries"] as const,
    states: (country: string) => [...QueryKeys.countries.all, "states", country] as const,
    cities: (country: string, state: string) =>
      [...QueryKeys.countries.all, "cities", country, state] as const,
  },

  // Attendance
  attendance: {
    all: ["attendance"] as const,
    lists: () => [...QueryKeys.attendance.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.attendance.lists(), params] as const,
    summaries: () => [...QueryKeys.attendance.all, "summary"] as const,
    summary: (params: unknown) => [...QueryKeys.attendance.summaries(), params] as const,
  },

  // Students
  students: {
    all: ["students"] as const,
    lists: () => [...QueryKeys.students.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.students.lists(), params] as const,
    details: () => [...QueryKeys.students.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.students.details(), id] as const,
    documents: (id: string) => [...QueryKeys.students.all, "documents", id] as const,
    health: (id: string) => [...QueryKeys.students.all, "health", id] as const,
    history: (id: string) => [...QueryKeys.students.all, "history", id] as const,
  },

  // Teacher Assignments
  teacherAssignments: {
    all: ["teacherAssignments"] as const,
    sectionAssignments: (academicYearId: string, sectionId: string) =>
      [
        ...QueryKeys.teacherAssignments.all,
        "section",
        academicYearId,
        sectionId,
      ] as const,
    teacherList: (academicYearId: string, search?: string, page?: number) =>
      [
        ...QueryKeys.teacherAssignments.all,
        "teachers",
        academicYearId,
        search ?? "",
        page ?? 1,
      ] as const,
    teacherDetail: (academicYearId: string, teacherEmployeeId: string) =>
      [
        ...QueryKeys.teacherAssignments.all,
        "detail",
        academicYearId,
        teacherEmployeeId,
      ] as const,
  },

  // Homework Assignments
  homeworkAssignments: {
    all: ["homeworkAssignments"] as const,
    lists: () => [...QueryKeys.homeworkAssignments.all, "list"] as const,
    list: (params: unknown) =>
      [...QueryKeys.homeworkAssignments.lists(), params] as const,
    details: () => [...QueryKeys.homeworkAssignments.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.homeworkAssignments.details(), id] as const,
  },

  // Timetable
  timetable: {
    all: ["timetable"] as const,
    lists: () => [...QueryKeys.timetable.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.timetable.lists(), params] as const,
  },

  // Timetable slot templates (slot master)
  slotTemplates: {
    all: ["slotTemplates"] as const,
    catalog: () => [...QueryKeys.slotTemplates.all, "catalog"] as const,
  },

  // Departments
  departments: {
    all: ["departments"] as const,
    lists: () => [...QueryKeys.departments.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.departments.lists(), params] as const,
    details: () => [...QueryKeys.departments.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.departments.details(), id] as const,
  },

  // Employees
  employees: {
    all: ["employees"] as const,
    lists: () => [...QueryKeys.employees.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.employees.lists(), params] as const,
    details: () => [...QueryKeys.employees.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.employees.details(), id] as const,
  },

  // Fee Types
  feeTypes: {
    all: ["feeTypes"] as const,
    lists: () => [...QueryKeys.feeTypes.all, "list"] as const,
    list: () => [...QueryKeys.feeTypes.lists()] as const,
  },

  // Fee Structures
  feeStructures: {
    all: ["feeStructures"] as const,
    lists: () => [...QueryKeys.feeStructures.all, "list"] as const,
    list: () => [...QueryKeys.feeStructures.lists()] as const,
    details: () => [...QueryKeys.feeStructures.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.feeStructures.details(), id] as const,
  },

  // Late Fine Rules
  lateFineRules: {
    all: ["lateFineRules"] as const,
    lists: () => [...QueryKeys.lateFineRules.all, "list"] as const,
    list: () => [...QueryKeys.lateFineRules.lists()] as const,
  },

  // Concessions
  concessions: {
    all: ["concessions"] as const,
    byStudent: (studentId: string) =>
      [...QueryKeys.concessions.all, "student", studentId] as const,
    rules: () => [...QueryKeys.concessions.all, "rules"] as const,
    assigned: (params: unknown) =>
      [...QueryKeys.concessions.all, "assigned", params] as const,
    students: (params: unknown) =>
      [...QueryKeys.concessions.all, "students", params] as const,
  },

  // Refunds
  refunds: {
    all: ["refunds"] as const,
    byPayment: (paymentId: string) =>
      [...QueryKeys.refunds.all, "payment", paymentId] as const,
  },

  // Account Heads
  accountHeads: {
    all: ["accountHeads"] as const,
    lists: () => [...QueryKeys.accountHeads.all, "list"] as const,
    list: (params: { search?: string }) =>
      [...QueryKeys.accountHeads.lists(), params] as const,
    details: () => [...QueryKeys.accountHeads.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.accountHeads.details(), id] as const,
  },

  accountVouchers: {
    all: ["accountVouchers"] as const,
    lists: () => [...QueryKeys.accountVouchers.all, "list"] as const,
    list: (params: {
      fromDate?: string;
      toDate?: string;
      voucherType?: string;
      search?: string;
      includeVoided?: boolean;
    }) => [...QueryKeys.accountVouchers.lists(), params] as const,
    details: () => [...QueryKeys.accountVouchers.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.accountVouchers.details(), id] as const,
  },

  // Vouchers
  vouchers: {
    all: ["vouchers"] as const,
    lists: () => [...QueryKeys.vouchers.all, "list"] as const,
    list: (params: Record<string, unknown>) =>
      [...QueryKeys.vouchers.lists(), params] as const,
    summaries: () => [...QueryKeys.vouchers.all, "summary"] as const,
    summary: (params: Record<string, unknown>) =>
      [...QueryKeys.vouchers.summaries(), params] as const,
  },

  // Finance Reports
  financeReports: {
    all: ["financeReports"] as const,
    dailyCollection: (from: string, to: string) =>
      [...QueryKeys.financeReports.all, "daily-collection", from, to] as const,
    dailyPaymentSummary: (fromDate: string, toDate: string) =>
      [
        ...QueryKeys.financeReports.all,
        "daily-payment-summary",
        fromDate,
        toDate,
      ] as const,
    outstanding: (classId: string | null) =>
      [...QueryKeys.financeReports.all, "outstanding", classId ?? "all"] as const,
    paidHistory: (params: unknown) =>
      [...QueryKeys.financeReports.all, "paid-history", params] as const,
    paidHistoryDetail: (paymentId: string, academicYearId: string) =>
      [
        ...QueryKeys.financeReports.all,
        "paid-history-detail",
        paymentId,
        academicYearId,
      ] as const,
  },

  // Payments Dashboard
  payments: {
    all: ["payments"] as const,
    overview: (params: unknown) =>
      [...QueryKeys.payments.all, "overview", params] as const,
    students: (params: unknown) =>
      [...QueryKeys.payments.all, "students", params] as const,
    summary: (studentId: string, academicYearId?: string | null) =>
      [
        ...QueryKeys.payments.all,
        "summary",
        studentId,
        academicYearId ?? "all-years",
      ] as const,
    history: (studentId: string) =>
      [...QueryKeys.payments.all, "history", studentId] as const,
    cheques: (params: unknown) => [...QueryKeys.payments.all, "cheques", params] as const,
    onlineAttempt: (attemptId: string) =>
      [...QueryKeys.payments.all, "online-attempt", attemptId] as const,
    gatewaySettings: () => [...QueryKeys.payments.all, "gateway-settings"] as const,
    ledgerStatus: (params: { academicYearId: string; classId: string }) =>
      [...QueryKeys.payments.all, "ledger-status", params] as const,
    ledgerStatusList: (params: { academicYearId: string }) =>
      [...QueryKeys.payments.all, "ledger-status-list", params] as const,
    installments: (studentId: string, academicYearId?: string) =>
      [...QueryKeys.payments.all, "installments", studentId, academicYearId] as const,
  },

  // Home dashboard
  dashboard: {
    all: ["dashboard"] as const,
    summary: (academicYearId?: string | null) =>
      [...QueryKeys.dashboard.all, "summary", academicYearId ?? "active"] as const,
  },

  notifications: {
    all: ["notifications"] as const,
    unreadCount: () => [...QueryKeys.notifications.all, "unread-count"] as const,
    audience: (mode: string, search: string) =>
      [...QueryKeys.notifications.all, "audience", mode, search] as const,
    history: (params: unknown) =>
      [...QueryKeys.notifications.all, "history", params] as const,
    recipients: (notificationId: string) =>
      [...QueryKeys.notifications.all, "history", notificationId, "recipients"] as const,
  },

  scheduledNotifications: {
    all: ["scheduledNotifications"] as const,
    lists: () => [...QueryKeys.scheduledNotifications.all, "list"] as const,
    list: (params: unknown) =>
      [...QueryKeys.scheduledNotifications.lists(), params] as const,
  },

  tenantProfile: {
    all: ["tenantProfile"] as const,
    current: () => [...QueryKeys.tenantProfile.all, "current"] as const,
  },

  admissions: {
    all: ["admissions"] as const,
    enquiries: (params: Record<string, unknown>) =>
      [...QueryKeys.admissions.all, "enquiries", params] as const,
    enquiryDetail: (enquiryId: string | null | undefined) =>
      [...QueryKeys.admissions.all, "enquiry-detail", enquiryId ?? "none"] as const,
    publicEnquiryForm: (tenantReference: string | null | undefined) =>
      [
        ...QueryKeys.admissions.all,
        "public-enquiry-form",
        tenantReference ?? "none",
      ] as const,
    latestTemplate: (
      tenantId: string | null | undefined,
      academicYearId: string | null | undefined,
    ) =>
      [
        ...QueryKeys.admissions.all,
        "latest-template",
        tenantId ?? "no-tenant",
        academicYearId ?? "no-academic-year",
      ] as const,
  },

  calendar: {
    all: ["calendar"] as const,
    lists: () => [...QueryKeys.calendar.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.calendar.lists(), params] as const,
  },

  eventsHolidays: {
    all: ["eventsHolidays"] as const,
    lists: () => [...QueryKeys.eventsHolidays.all, "list"] as const,
    list: (params: unknown) => [...QueryKeys.eventsHolidays.lists(), params] as const,
  },

  certificates: {
    all: ["certificates"] as const,
    templates: () => [...QueryKeys.certificates.all, "templates"] as const,
    generated: () => [...QueryKeys.certificates.all, "generated"] as const,
  },

  communicationTemplates: {
    all: ["communicationTemplates"] as const,
    lists: () => [...QueryKeys.communicationTemplates.all, "list"] as const,
    list: (params: unknown) =>
      [...QueryKeys.communicationTemplates.lists(), params] as const,
    details: () => [...QueryKeys.communicationTemplates.all, "detail"] as const,
    detail: (id: string) => [...QueryKeys.communicationTemplates.details(), id] as const,
  },

  notificationSettings: {
    all: ["notificationSettings"] as const,
    settings: () => [...QueryKeys.notificationSettings.all, "settings"] as const,
    channels: () => [...QueryKeys.notificationSettings.all, "channels"] as const,
    events: () => [...QueryKeys.notificationSettings.all, "events"] as const,
  },

  whatsapp: {
    all: ["whatsapp"] as const,
    account: () => [...QueryKeys.whatsapp.all, "account"] as const,
    signupConfig: () => [...QueryKeys.whatsapp.all, "signupConfig"] as const,
    templates: (filters?: unknown) =>
      [...QueryKeys.whatsapp.all, "templates", filters ?? null] as const,
    recommendedTemplates: () =>
      [...QueryKeys.whatsapp.all, "recommendedTemplates"] as const,
    messages: (filters?: unknown) =>
      [...QueryKeys.whatsapp.all, "messages", filters ?? null] as const,
    dispatchSummary: (dispatchId: string) =>
      [...QueryKeys.whatsapp.all, "dispatchSummary", dispatchId] as const,
    usage: (params: unknown) =>
      [...QueryKeys.whatsapp.all, "usage", params ?? null] as const,
    budget: () => [...QueryKeys.whatsapp.all, "budget"] as const,
  },

  platformWhatsApp: {
    all: ["platformWhatsApp"] as const,
    accounts: () => [...QueryKeys.platformWhatsApp.all, "accounts"] as const,
    rateCard: (countryCode?: string) =>
      [...QueryKeys.platformWhatsApp.all, "rateCard", countryCode ?? null] as const,
  },

  platformMessaging: {
    all: ["platformMessaging"] as const,
    endpoints: () => [...QueryKeys.platformMessaging.all, "endpoints"] as const,
    deadLetters: (endpoint: string) =>
      [...QueryKeys.platformMessaging.all, "deadLetters", endpoint] as const,
    outbox: () => [...QueryKeys.platformMessaging.all, "outbox"] as const,
  },

  communicationContacts: {
    all: ["communicationContacts"] as const,
    lists: () => [...QueryKeys.communicationContacts.all, "list"] as const,
    list: (params: unknown) =>
      [...QueryKeys.communicationContacts.lists(), params] as const,
    summary: () => [...QueryKeys.communicationContacts.all, "summary"] as const,
  },

  inbox: {
    all: ["inbox"] as const,
    conversations: (params: unknown) =>
      [...QueryKeys.inbox.all, "conversations", params] as const,
    conversation: (id: string) => [...QueryKeys.inbox.all, "conversation", id] as const,
    messages: (id: string) => [...QueryKeys.inbox.all, "messages", id] as const,
    assignableStaff: (search: string) =>
      [...QueryKeys.inbox.all, "assignable-staff", search] as const,
  },

  // Ask Trovix: every key carries the scope (tenant, user, active context, academic year) the conversations belong to.
  askTrovix: {
    all: ["askTrovix"] as const,
    scoped: (scope: unknown) => [...QueryKeys.askTrovix.all, scope] as const,
    tools: (scope: unknown) => [...QueryKeys.askTrovix.scoped(scope), "tools"] as const,
    conversations: (scope: unknown) =>
      [...QueryKeys.askTrovix.scoped(scope), "conversations"] as const,
    conversation: (scope: unknown, conversationId: string) =>
      [...QueryKeys.askTrovix.scoped(scope), "conversation", conversationId] as const,
    ask: (scope: unknown) => [...QueryKeys.askTrovix.scoped(scope), "ask"] as const,
  },

  // Ask Trovix settings (the school's read-only view): keyed by tenant and user.
  askTrovixSettings: {
    all: ["askTrovixSettings"] as const,
    settings: (scope: unknown) =>
      [...QueryKeys.askTrovixSettings.all, scope, "settings"] as const,
    usage: (scope: unknown) =>
      [...QueryKeys.askTrovixSettings.all, scope, "usage"] as const,
  },
} as const;

export type QueryKey = typeof QueryKeys;
